import React from "react";
import {
  FaTrophy,
  FaEnvelope,
  FaPhoneAlt,
  FaLinkedin,
  FaGlobe,
  FaMapMarkerAlt,
  FaLightbulb,
} from "react-icons/fa";
import "./ImageTemplate.css";

const ImageMatchTemplate = ({ resumeData }) => {
  if (!resumeData) return <div className="loading-placeholder">Loading...</div>;

  const {
    personal = {},
    education = [],
    experience = [],
    skills = { technical: [], soft: [], languages: [] },
    achievements = [],
    interests = [],
    certifications = [],
  } = resumeData;

  // Combine all skills for sidebar display
  const allSkills = [...skills.technical, ...skills.soft];

  return (
    <div className="resume-template image-template">
      {/* LEFT SIDEBAR (Dark) */}
      <aside className="sidebar">
        {/* Profile Photo */}
        {personal.photo && (
          <div className="profile-photo">
            <img src={personal.photo} alt={personal.fullName || "Profile"} />
          </div>
        )}

        <div className="sidebar-header">
          <h1 className="user-name">{personal.fullName || "YOUR NAME"}</h1>
        </div>

        {/* Key Achievements */}
        {achievements.length > 0 && (
          <section className="sidebar-section">
            <h3 className="section-title">KEY ACHIEVEMENTS</h3>
            <div className="achievement-list">
              {achievements.map((ach, idx) => (
                <div key={idx} className="achievement-item">
                  <div className="ach-icon">
                    <FaTrophy />
                  </div>
                  <div className="ach-text">
                    <p className="ach-title">
                      {ach.split(" ").slice(0, 2).join(" ")}
                    </p>
                    <p className="ach-desc">{ach}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Education */}
        {education.length > 0 && (
          <section className="sidebar-section">
            <h3 className="section-title">EDUCATION</h3>
            {education.map((edu, idx) => (
              <div key={edu.id || idx} className="sidebar-item">
                <p className="bold-text">{edu.degree || "Degree"}</p>
                <p>{edu.institution || "University"}</p>
                <div className="side-row">
                  <span>{edu.year || "Year"}</span>
                  <span>{edu.location || "Location"}</span>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Skills (Technical + Soft) */}
        {allSkills.length > 0 && (
          <section className="sidebar-section">
            <h3 className="section-title">SKILLS</h3>
            <div className="skills-list-sidebar">
              {allSkills.map((skill, idx) => (
                <p key={idx} className="skill-item-sidebar">
                  {skill}
                </p>
              ))}
            </div>
          </section>
        )}

        {/* Certifications / Courses */}
        {certifications.length > 0 && (
          <section className="sidebar-section">
            <h3 className="section-title">TRAINING / COURSES</h3>
            {certifications.map((cert, idx) => (
              <div key={cert.id || idx} className="sidebar-item">
                <p className="bold-text">{cert.name}</p>
                <p className="small-text-sidebar">{cert.issuer}</p>
              </div>
            ))}
          </section>
        )}
      </aside>

      {/* RIGHT MAIN CONTENT */}
      <main className="main-content">
        <header className="main-header">
          <h2 className="role-title">{personal.title || "Target Role"}</h2>
          <div className="contact-info-row">
            {personal.email && (
              <span>
                <FaEnvelope /> {personal.email}
              </span>
            )}
            {personal.phone && (
              <span>
                <FaPhoneAlt /> {personal.phone}
              </span>
            )}
            {personal.linkedin && (
              <span>
                <FaLinkedin /> LinkedIn
              </span>
            )}
            {personal.portfolio && (
              <span>
                <FaGlobe /> {personal.portfolio}
              </span>
            )}
            {personal.address && (
              <span>
                <FaMapMarkerAlt /> {personal.address}
              </span>
            )}
          </div>
        </header>

        {/* Summary */}
        {personal.summary && (
          <section className="main-section">
            <h3 className="main-section-title">SUMMARY</h3>
            <p className="summary-text">{personal.summary}</p>
          </section>
        )}

        {/* Experience */}
        {experience.length > 0 && (
          <section className="main-section">
            <h3 className="main-section-title">EXPERIENCE</h3>
            {experience.map((exp, idx) => (
              <div key={exp.id || idx} className="exp-item">
                <div className="exp-header">
                  <span className="bold-text">{exp.title}</span>
                  <span className="date-text">
                    {exp.startDate} - {exp.current ? "Present" : exp.endDate}
                  </span>
                </div>
                <div className="exp-sub-header">
                  <span className="company-name">{exp.company}</span>
                  <span className="location-text">{exp.location}</span>
                </div>
                <ul className="exp-points">
                  {exp.description?.split("\n").map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}

        {/* Interests */}
        {interests.length > 0 && (
          <section className="main-section">
            <h3 className="main-section-title">INTERESTS</h3>
            <div className="interests-grid">
              {interests.map((interest, idx) => (
                <div key={idx} className="interest-item-box">
                  <div className="int-icon-circle">
                    <FaLightbulb />
                  </div>
                  <div>
                    <p className="bold-text">{interest}</p>
                    <p className="small-text-gray">Personal Passion</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Languages */}
        {skills.languages?.length > 0 && (
          <section className="main-section">
            <h3 className="main-section-title">LANGUAGES</h3>
            <div className="languages-container">
              {skills.languages.map((lang, idx) => (
                <div key={idx} className="lang-row">
                  <span className="lang-name">{lang}</span>
                  <span className="lang-dots">●●●●●</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default ImageMatchTemplate;
